import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cherry, Apple, TreeDeciduous, Sprout, Snowflake, Thermometer } from "lucide-react";

export function useAg() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/ag").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 1_800_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const CROP_ICON = { Peaches: Cherry, Apples: Apple, Pecans: TreeDeciduous, "Vidalia Onions": Sprout };
const TONE = { good: "#16a34a", watch: "#f59e0b", bad: "#dc2626", none: "#64748b" };

function CropCard({ c }) {
  const Icon = CROP_ICON[c.crop] || Sprout;
  const tone = TONE[c.freeze?.tone] || TONE.none;
  const hasChill = c.chill_goal != null;
  return (
    <div className="flex h-full items-center gap-7 rounded-[8px] bg-white px-9 py-6 shadow-[0_12px_30px_rgba(0,0,0,0.22)]" style={{ borderLeft: `16px solid ${tone}` }} data-testid={`ag-crop-${c.crop.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="h-24 w-24 shrink-0 text-[#b45309]" />
      <div className="min-w-0 flex-1">
        <div className="font-oswald text-[48px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{c.crop}</div>
        <div className="mb-3 mt-1 font-oswald text-[26px] font-semibold uppercase tracking-wide text-[#5b6675]">{c.region} · {c.city}</div>
        {hasChill ? (
          <>
            <div className="h-[22px] w-full overflow-hidden rounded-full bg-[#e7ebf1]">
              <div className="h-full rounded-full bg-gradient-to-r from-[#1f63c9] to-[#2a74d8]" style={{ width: `${c.chill_pct || 0}%` }} />
            </div>
            <div className="mt-2 font-oswald text-[27px] font-bold uppercase tracking-wide text-[#2c3542]">
              <Snowflake className="mr-1.5 inline h-6 w-6 text-[#2a74d8]" />{c.chill_hours ?? "—"} / {c.chill_goal} chill hrs <span className="text-[#5b6675]">({c.chill_pct ?? 0}%)</span>
            </div>
          </>
        ) : (
          <div className="font-oswald text-[27px] font-bold uppercase tracking-wide text-[#5b6675]">
            <Snowflake className="mr-1.5 inline h-6 w-6 text-[#94a3b8]" />Chill N/A · freeze-sensitive crop
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="font-oswald text-[24px] font-bold uppercase tracking-widest text-[#5b6675]">Low</div>
        <div className="flex items-center justify-end gap-1 font-oswald text-[92px] font-black leading-none text-[#0a0f1c]">
          <Thermometer className="h-12 w-12" style={{ color: tone }} />{c.upcoming_low ?? "—"}°
        </div>
        <div className="mt-2 inline-block rounded-[4px] px-3 py-1 font-oswald text-[23px] font-black uppercase tracking-wider text-white" style={{ background: tone }}>{c.freeze?.label}</div>
      </div>
    </div>
  );
}

export function AgSlide({ ag }) {
  const crops = ag?.crops || [];
  if (!crops.length) return null;
  return (
    <div data-testid="ag-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #1b3a1e 0%, #2f5d2a 45%, #7a5a1e 100%)" }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#ffffff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
      <div className="relative flex h-full w-full flex-col px-[52px] pt-[24px] pb-[52px]">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <div className="font-oswald text-[23px] font-bold uppercase tracking-[0.3em] text-[#ffd60a]">Georgia Ag Weather</div>
            <div className="font-oswald text-[56px] font-black uppercase leading-none tracking-wide text-white">Peach · Pecan · Harvest Watch</div>
          </div>
          <div className="text-right font-oswald text-[22px] font-bold uppercase tracking-widest text-white/70">Chill Hours<br /><span className="text-white/90">since Oct 1</span></div>
        </div>
        <div className="grid flex-1 grid-cols-2 auto-rows-fr gap-x-6 gap-y-5">
          {crops.map((c) => <CropCard key={c.crop} c={c} />)}
        </div>
      </div>
      <div className="absolute bottom-[16px] left-[52px] font-oswald text-[20px] font-semibold uppercase tracking-[0.18em] text-white/60">{ag.source}</div>
    </div>
  );
}
